/** @file Create structure units section for Structure Form */

import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Link, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';

import type { Unit } from '../../../Units/types';
import { sortByIndex } from '../../util';

type UnitCardProps = {
  className: string;
  name?: string;
  onNameChange: (name: string) => void;
  isValid: boolean;
};

/**
 * Unit Card
 *
 * @param {object} props - Component properties
 * @returns {React.Component} - Unit Card
 */
const UnitCard = (props: UnitCardProps) => {
  return (
    <TextField
      className={props.className}
      label={'Unit Name'}
      name={'id'}
      required={true}
      value={props.name}
      onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
        props.onNameChange(getEventTarget(evt).value);
      }}
    />
  );
};

/**
 * Get correct event target
 *
 * @param  {Event} evt - Event
 * @returns {HTMLElement} - Resolved event target
 */
const getEventTarget = (evt) => evt.currentTarget || evt.target;

/**
 * Classname/style generator
 */
const useStyles: () => {
  root: string;
  input: string;
  sameRowInputContainer: string;
  cardInput: string;
  cardInputContainer: string;
} = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    margin: theme.spacing(1),
  },
  sameRowInputContainer: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  cardInput: {
    margin: theme.spacing(1),
    display: 'flex',
    marginTop: '15px',
  },
  cardInputContainer: {
    padding: '15px',
  },
}));

type Props = {
  onNameChange: (id: string, name: string) => void;
  onImportUnits: () => void;
  units: Unit[];
  unitErrorObj: { [id: string]: boolean };
};

export const CreateUnits = (props: Props): JSX.Element => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.cardInputContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <UploadFileIcon fontSize="small" color="primary" />
          <Link component="button" type="button" variant="body2" onClick={props.onImportUnits}>
            Import units from spreadsheet
          </Link>
        </Box>
        {props.units.sort(sortByIndex).map((unit) => (
          <UnitCard
            className={classes.cardInput}
            isValid={!props.unitErrorObj[unit.id]}
            key={unit.id}
            name={unit.name}
            onNameChange={(newName) => props.onNameChange(unit.id, newName)}
          />
        ))}
      </div>
    </div>
  );
};
